import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addItemToCart,
  removeItem,
  updateItemQuantity,
  setCartLoading,
  setCart, 
} from "../../redux/hejalSlice";

import toast from "react-hot-toast";
import { apiPaths } from "../../service/apiPaths";
import ApiService from "../../service/ApiService";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  Shield,
  Truck,
  Package,
  ArrowLeft,
  ChevronRight,
  Heart,
  Share,
  ShoppingCart,
  Check,
  Loader2,
  ShieldCheck,
  Lock,
  Zap,
  Eye,
  ThumbsUp,
} from "lucide-react";
import RatingsAndReviews from "../Ratings&Reviews/RatingsAndReviews";
import { ToggleWishlist } from "../../redux/wishlistSlice";

const ProductDetails = () => {
  const { productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const [activeImg, setActiveImg] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  
  const cartItems = useSelector((store) => store.hejal.cartItems);
  const isLoggedIn = useSelector((state) => state.hejal.isLoggedIn);
  const wishlist = useSelector((state) => state.wishlist.wishlistItems);

  // Fetch product details
  const fetchProduct = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getProductDetails}/${productId}`,
      });

      if (response.success && response.data) {
        const data = response.data;
        setProductData(data);
        
        // Set default size first (first available size)
        const firstSize = Object.keys(data.variants.bySize)[0];
        if (firstSize) {
          setSelectedSize(firstSize);
          
          // Set default color for the selected size (first available color for that size)
          const sizeData = data.variants.bySize[firstSize];
          const firstAvailableColor = sizeData.colors.find(c => c.inventory?.stock > 0);
          
          if (firstAvailableColor) {
            setSelectedColor(firstAvailableColor.color.name);
            
            // Set variant
            setSelectedVariant({
              variantId: firstAvailableColor.variantId,
              sku: firstAvailableColor.sku,
              size: sizeData.sizeInfo,
              pricing: firstAvailableColor.pricing,
              inventory: firstAvailableColor.inventory
            });
            
            // Set the first image from the color
            const colorData = data.variants.byColor[firstAvailableColor.color.name];
            if (colorData && colorData.colorInfo.images.length > 0) {
              setActiveImg(colorData.colorInfo.images[0]);
            }
          }
        }

        // UPDATED: Set wishlist status using backend data
        if (isLoggedIn && data.wishlist) {
          setIsFavorite(data.wishlist.isInWishlist);
        }
        
      } else {
        toast.error("Product not found");
        navigate('/');
      }
    } catch (err) {
      toast.error("Error while fetching product details");
      console.error("Error fetching product:", err);
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  }, [productId, navigate, isLoggedIn]);

  useEffect(() => {
    fetchProduct();
    window.scrollTo(0, 0);
  }, [fetchProduct, productId]);

  // Handle size and color selection
  const handleSizeSelect = (sizeName) => {
    setSelectedSize(sizeName);
    
    const sizeData = productData.variants.bySize[sizeName];
    if (!sizeData || sizeData.colors.length === 0) {
      setSelectedVariant(null);
      setSelectedColor("");
      return;
    }
    
    const currentColorForSize = sizeData.colors.find(c => c.color.name === selectedColor);
    
    if (currentColorForSize && currentColorForSize.inventory?.stock > 0) {
      setSelectedVariant({
        variantId: currentColorForSize.variantId,
        sku: currentColorForSize.sku,
        size: sizeData.sizeInfo,
        pricing: currentColorForSize.pricing,
        inventory: currentColorForSize.inventory
      });
    } else {
      const firstAvailableColor = sizeData.colors.find(c => c.inventory?.stock > 0);
      if (firstAvailableColor) {
        setSelectedColor(firstAvailableColor.color.name);
        
        const colorData = productData.variants.byColor[firstAvailableColor.color.name];
        if (colorData && colorData.colorInfo.images.length > 0) {
          setActiveImg(colorData.colorInfo.images[0]);
        }
        
        setSelectedVariant({
          variantId: firstAvailableColor.variantId,
          sku: firstAvailableColor.sku,
          size: sizeData.sizeInfo,
          pricing: firstAvailableColor.pricing,
          inventory: firstAvailableColor.inventory
        });
      } else {
        setSelectedColor("");
        setSelectedVariant(null);
      }
    }

    // UPDATED: Update wishlist status when variant changes
    updateWishlistStatus();
  };

  const handleColorSelect = (colorName) => {
    if (!selectedSize) {
      toast.warning("Please select a size first");
      return;
    }
    
    const sizeData = productData.variants.bySize[selectedSize];
    if (!sizeData) return;
    
    const colorForSize = sizeData.colors.find(c => c.color.name === colorName);
    if (!colorForSize || colorForSize.inventory?.stock <= 0) {
      toast.warning(`${colorName} is not available in ${selectedSize} size`);
      return;
    }
    
    setSelectedColor(colorName);
    
    const colorData = productData.variants.byColor[colorName];
    if (colorData && colorData.colorInfo.images.length > 0) {
      setActiveImg(colorData.colorInfo.images[0]);
    }
    
    setSelectedVariant({
      variantId: colorForSize.variantId,
      sku: colorForSize.sku,
      size: sizeData.sizeInfo,
      pricing: colorForSize.pricing,
      inventory: colorForSize.inventory
    });

    // UPDATED: Update wishlist status when variant changes
    updateWishlistStatus();
  };

  // NEW: Function to update wishlist status based on current selection and backend data
  const updateWishlistStatus = useCallback(() => {
    if (!isLoggedIn || !productData?.wishlist) {
      setIsFavorite(false);
      return;
    }

    const { isInWishlist } = productData.wishlist;

    if(isInWishlist){
      setIsFavorite(true);
    }else{
      setIsFavorite(false);
    }
    
    
  }, [isLoggedIn, productData?.wishlist, selectedVariant?.sku]);

  // UPDATED: Effect to update wishlist status when dependencies change
  useEffect(() => {
    updateWishlistStatus();
  }, [updateWishlistStatus]);

  // UPDATED: Also update when Redux wishlist changes (for real-time updates)
 
  const handleImageActive = (img) => {
    setActiveImg(img);
  };

  // Cart logic
  const cartContainsProduct = useCallback(() => {
    if (!productId || !cartItems) return null;
    
    return cartItems.find((item) => {
      if (item.itemType === 'combo') return false;
      
      const itemProductId = item.product?._id || item.productId?._id || item.productId;
      const itemVariantId = item.variant?._id || item.variantId;
      const selectedVariantId = selectedVariant?.variantId;
      
      if (selectedVariantId && itemVariantId) {
        return itemProductId === productId && itemVariantId === selectedVariantId;
      }
      
      return itemProductId === productId;
    });
  }, [cartItems, productId, selectedVariant]);

  const cartContainsCombo = useCallback((comboName) => {
    if (!productId || !cartItems) return null;
    
    return cartItems.find((item) => {
      return item.itemType === 'combo' && 
             item.comboDetails?.productId === productId &&
             item.comboDetails?.comboName === comboName;
    });
  }, [cartItems, productId]);

  // Add single product to cart
  const addProductToCart = async () => {
    if (!isLoggedIn) {
      toast.error("Please Log In");
      navigate("/signin");
      return;
    }

    if (!selectedVariant) {
      toast.error("Please select color and size");
      return;
    }

    if (selectedVariant?.inventory?.stock && selectedVariant.inventory.stock <= 0) {
      toast.error("This variant is currently out of stock");
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.addToCart,
        data: {
          productId: productId,
          variantSku: selectedVariant.sku,
          quantity: Number(quantity)
        },
      });

      if (response.success) {
        const cartResponse = await ApiService({
          method: "GET",
          endpoint: apiPaths.getCartProducts,
        });

        if (cartResponse.success && cartResponse.data) {
          dispatch(setCart(cartResponse.data));
        }

        toast.success("Product added to cart");
      } else {
        toast.error(response.message || "Failed to add product to cart");
      }
          
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add product to cart");
      console.error("Error adding to cart:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Add combo to cart
  const addComboToCart = async (combo) => {
    if (!isLoggedIn) {
      toast.error("Please Log In");
      navigate("/signin");
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.addComboToCart,
        data: {
          productId: productId,
          comboName: combo.name,
          comboPrice: combo.comboPrice,
          items: combo.comboItems ? combo.comboItems.map(item => ({
            sizeCode: item.sizeCode,
            colorCode: item.colorCode,
            variantSku: item.variantSku,
            quantity: item.quantity
          })) : combo.includedSizes.map(sizeItem => {
            let sizeName = sizeItem.sizeCode;
            if (sizeItem.sizeCode === 'S') sizeName = 'Small';
            else if (sizeItem.sizeCode === 'M') sizeName = 'Medium';  
            else if (sizeItem.sizeCode === 'L') sizeName = 'Large';
            
            const sizeData = productData.variants.bySize[sizeName];
            const availableColors = sizeData.colors.filter(c => c.inventory?.stock > 0);
            const cheapestColor = availableColors.sort((a, b) => 
              (a.pricing.salePrice || a.pricing.basePrice) - (b.pricing.salePrice || b.pricing.basePrice)
            )[0];

            return {
              sizeCode: sizeItem.sizeCode,
              colorCode: cheapestColor.color.code,
              variantSku: cheapestColor.sku,
              quantity: sizeItem.quantity
            };
          })
        },
      });

      if (response.success) {
        const cartResponse = await ApiService({
          method: "GET",
          endpoint: apiPaths.getCartProducts,
        });

        if (cartResponse.success) {
          dispatch(setCart(cartResponse.data));
        }

        toast.success(`${combo.name} added to cart!`);
      } else {
        toast.error(response.message || "Failed to add combo to cart");
      }
          
    } catch (err) {
      toast.error("Failed to add combo to cart");
      console.error("Error adding combo:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Remove product from cart
  const removeProductFromCart = async () => {
    const cartItem = cartContainsProduct();
    if (!cartItem) return;

    setIsLoading(true);
    try {
      const response = await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.removeFromCart}/${cartItem._id}`,
      });

      if (response.success) {
        dispatch(removeItem({ cartItemId: cartItem._id }));
        toast.success("Product removed from cart");
      } else {
        toast.error("Failed to remove product");
      }
    } catch (err) {
      toast.error("Failed to remove product");
      console.error("Error removing from cart:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuantityChange = async (action) => {
    if (isLoading || !selectedVariant) return;

    let newQuantity = quantity;
    if (action === "increase") {
      if (newQuantity >= (selectedVariant.inventory?.stock || 0)) {
        toast.error("Cannot exceed available stock");
        return;
      }
      newQuantity += 1;
    } else if (action === "decrease") {
      if (newQuantity <= 1) return;
      newQuantity -= 1;
    }

    setQuantity(newQuantity);

    const cartItem = cartContainsProduct();
    if (cartItem) {
      setIsLoading(true);
      try {
        const response = await ApiService({
          method: "PUT",
          endpoint: `${apiPaths.updateQuantity}/${cartItem._id}`,
          data: { quantity: newQuantity },
        });

        if (response.success && response.data) {
          dispatch(updateItemQuantity({ 
            cartItemId: cartItem._id, 
            newQuantity: response.data.quantity || newQuantity
          }));
          
          if (!response.data.quantity) {
            setQuantity(newQuantity);
          }
        } else {
          setQuantity(cartItem.quantity);
          toast.error("Failed to update quantity");
        }
      } catch (err) {
        toast.error("Failed to update quantity");
        setQuantity(cartItem.quantity);
        console.error("Error updating quantity:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!imageRef.current || !showZoom) return;
    const { left, top, width, height } = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomPosition({ x, y });
  };

  // UPDATED: Toggle favorite function with better backend integration
  const toggleFavorite = async () => {
    if (!isLoggedIn) {
      toast.error("Please Log In");
      navigate("/signin");
      return;
    }

    if (!productData?.productInfo?._id) {
      toast.error("Product not found");
      return;
    }

    const wishlistData = {
      product: productData.productInfo,
      variantSku: selectedVariant?.sku || null,
      selectedVariant: selectedVariant ? {
        sku: selectedVariant.sku,
        size: selectedVariant.size,
        pricing: selectedVariant.pricing,
        inventory: selectedVariant.inventory
      } : null
    };

    // Optimistically update UI
    setIsFavorite(!isFavorite);
    dispatch(ToggleWishlist(wishlistData));
    
    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.wishlist,
        data: { 
          productId: productData.productInfo._id,
          variantSku: selectedVariant?.sku || null
        },
      });
      
      if (response.success) {
        toast.success(response.message || "Updated wishlist");
        
        // UPDATED: Update the productData with new wishlist info if provided by backend
        if (response.data?.wishlist) {
          setProductData(prev => ({
            ...prev,
            wishlist: response.data.wishlist
          }));
        }
      } else {
        // Revert on failure
        setIsFavorite(!isFavorite);
        dispatch(ToggleWishlist(wishlistData));
        toast.error(response.message || "Failed to update wishlist");
      }
    } catch (error) {
      // Revert on error
      setIsFavorite(!isFavorite);
      dispatch(ToggleWishlist(wishlistData));
      toast.error("Failed to update wishlist");
    }
  };

  const shareProduct = () => {
    if (navigator.share) {
      navigator
        .share({
          title: productData?.productInfo?.name,
          text: productData?.productInfo?.description,
          url: window.location.href,
        })
        .catch((error) => console.error("Error sharing:", error));
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  const discountPercentage = selectedVariant && selectedVariant.pricing
    ? Math.round(((selectedVariant.pricing.basePrice - selectedVariant.pricing.salePrice) / selectedVariant.pricing.basePrice) * 100)
    : 0;

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating || 0)
            ? "fill-orange-400 text-orange-400"
            : "fill-gray-200 text-gray-200"
        }`}
      />
    ));
  };

  // Loading and error states
  if (isLoading && !productData) {
    return (
      <div className="w-full min-h-[70vh] max-w-7xl mx-auto px-4 py-10 flex flex-col justify-center items-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#182134] mb-4" />
        <p className="text-xl">Loading product details...</p>
      </div>
    );
  }

  if (!productData && !isLoading) {
    return (
      <div className="w-full min-h-[70vh] max-w-7xl mx-auto px-4 py-10 flex flex-col justify-center items-center">
        <div className="text-gray-500 mb-4">
          <Package className="h-16 w-16 mx-auto" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Product Not Found</h2>
        <p className="text-gray-600 mb-6">
          The product you're looking for might be unavailable or doesn't exist.
        </p>
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-all"
        >
          <ArrowLeft size={18} />
          Back to Home
        </button>
      </div>
    );
  }

  const product = productData.productInfo;
  const variants = productData.variants;
  const ratings = productData.ratings;

  // Get available sizes
  const availableSizes = Object.keys(variants.bySize);
  
  // Get colors available for the currently selected size ONLY
  const getAvailableColorsForSize = (sizeName) => {
    if (!variants || !sizeName) return [];
    const sizeData = variants.bySize[sizeName];
    if (!sizeData) return [];
    
    return sizeData.colors
      .filter(color => color.inventory?.stock > 0)
      .map(color => color.color.name);
  };

  const availableColors = selectedSize ? getAvailableColorsForSize(selectedSize) : [];
  
  // Get current color images
  const currentColorImages = selectedColor && variants.byColor[selectedColor] ? variants.byColor[selectedColor].colorInfo.images : [];

  const cartItem = cartContainsProduct();
  const isInCart = !!cartItem;

  // UPDATED: Show variant-specific wishlist status in size/color options
  const isVariantInWishlist = (variantSku) => {
    return productData?.wishlist?.wishlistedVariants?.includes(variantSku) || false;
  };

  return (
    <div className="w-full mx-auto">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center text-sm text-gray-500">
            <span className="hover:text-[#182134] cursor-pointer" onClick={() => navigate("/")}>
              Home
            </span>
            <ChevronRight size={14} className="mx-2" />
            <span className="hover:text-[#182134] cursor-pointer">
              {product.category?.name || "Category"}
            </span>
            <ChevronRight size={14} className="mx-2" />
            <span className="text-blue-600 font-medium">
              {product.name}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="w-full grid grid-cols-12 gap-6 pt-8">
          {/* Left Section: Thumbnails */}
          <div className="xl:col-span-1 lg:col-span-1 md:col-span-12 col-span-12 order-1">
            {currentColorImages.length > 1 && (
              <div className="flex md:flex-col flex-row gap-2 md:gap-3">
                <AnimatePresence>
                  {currentColorImages.map((img, i) => (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.1 }}
                      onClick={() => handleImageActive(img)}
                      className={`border h-[60px] w-[60px] md:h-[80px] md:w-[80px] p-1 overflow-hidden rounded-lg ${
                        img === activeImg
                          ? "ring-2 ring-blue-500 border-transparent"
                          : "border-gray-200 hover:border-gray-300"
                      } transition-all duration-200 bg-white`}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${i + 1}`}
                        className="h-full w-full object-contain"
                      />
                    </motion.button>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Center Section: Main Image */}
          <div className="xl:col-span-5 lg:col-span-5 md:col-span-12 col-span-12 order-2">
            <div className="bg-white overflow-hidden">
              <motion.div
                className="relative overflow-hidden flex items-center justify-center"
                style={{ height: "500px" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="relative h-full w-full flex items-center justify-center p-4"
                  onMouseEnter={() => setShowZoom(true)}
                  onMouseLeave={() => setShowZoom(false)}
                  onMouseMove={handleMouseMove}
                  ref={imageRef}
                >
                  <img
                    className="h-full w-full object-contain"
                    src={activeImg}
                    alt={product.name}
                  />

                  {/* Zoom overlay */}
                  {showZoom && (
                    <div
                      className="absolute inset-0 bg-no-repeat pointer-events-none"
                      style={{
                        backgroundImage: `url(${activeImg})`,
                        backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                        backgroundSize: "200%",
                      }}
                    />
                  )}
                </div>

                {/* Action buttons */}
                <div className="absolute top-8 right-8 flex flex-col gap-3">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleFavorite}
                    className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${
                      isFavorite ? "bg-red-500 text-white" : "bg-white text-gray-700"
                    }`}
                  >
                    <Heart size={18} fill={isFavorite ? "white" : "none"} />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={shareProduct}
                    className="w-10 h-10 rounded-full bg-white text-gray-700 flex items-center justify-center shadow-md"
                  >
                    <Share size={18} />
                  </motion.button>
                </div>

                {/* Discount badge */}
                {discountPercentage > 0 && (
                  <span className="absolute top-8 left-8 bg-red-500 text-white px-3 py-1 text-sm font-bold rounded">
                    {discountPercentage}% OFF
                  </span>
                )}
              </motion.div>
            </div>
          </div>

          {/* Right Section: Product Info */}
          <div className="xl:col-span-6 lg:col-span-6 md:col-span-12 col-span-12 order-3">
            <div className="bg-white p-6">
              {/* Product Title */}
              <div className="pb-4">
                <h1 className="text-xl md:text-xl mb-3 text-gray-900">
                  {product.name}
                </h1>

                {/* Price Section */}
                <div className="mb-4">
                  {selectedVariant && (
                    <div className="flex items-baseline gap-3">
                      <h2 className="text-xl text-gray-900">
                        ₹{selectedVariant.pricing?.salePrice || selectedVariant.pricing?.basePrice}
                      </h2>
                      {selectedVariant.pricing?.basePrice && selectedVariant.pricing?.salePrice && 
                       selectedVariant.pricing.basePrice > selectedVariant.pricing.salePrice && (
                        <>
                          <p className="text-xl text-gray-400 line-through">
                            ₹{selectedVariant.pricing.basePrice?.toLocaleString()}
                          </p>
                          <p className="text-red-500 font-medium">
                            {discountPercentage}% OFF
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">(Inclusive of all taxes)</p>
                </div>              
              </div>

              {/* Size Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">
                  Size: <span className="capitalize text-gray-600">{selectedSize || 'Please select'}</span>
                </label>
                <div className="flex gap-2 flex-wrap">
                  {availableSizes.map((sizeName) => {
                    const sizeData = variants.bySize[sizeName];
                    const availableColorsForSize = sizeData?.colors?.filter(color => 
                      color.inventory?.stock > 0
                    ) || [];
                    const isAvailable = availableColorsForSize.length > 0;
                    
                    return (
                      <button
                        key={sizeName}
                        onClick={() => handleSizeSelect(sizeName)}
                        disabled={!isAvailable}
                        className={`px-4 py-2 border text-sm font-medium transition-all ${
                          selectedSize === sizeName
                            ? "border-gray-900 bg-gray-900 text-white"
                            : isAvailable
                              ? "border-gray-300 hover:border-gray-400 bg-white text-gray-900"
                              : "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                        }`}
                      >
                        <div className="flex flex-col items-center">
                          <span>{sizeName}</span>
                          <span className="text-xs opacity-75">
                            {availableColorsForSize.length} color{availableColorsForSize.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Color Selection - UPDATED with wishlist indicators */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">
                  Color: <span className="capitalize text-gray-600">{selectedColor || 'Please select'}</span>
                  {selectedSize && (
                    <span className="text-xs text-gray-500 ml-2">
                      (for size {selectedSize})
                    </span>
                  )}
                </label>
                
                {selectedSize ? (
                  <div className="flex gap-2">
                    {availableColors.length > 0 ? (
                      availableColors.map((colorName) => {
                        const colorInfo = variants.byColor[colorName].colorInfo;
                        const sizeData = variants.bySize[selectedSize];
                        const colorForSize = sizeData.colors.find(c => c.color.name === colorName);
                        const variantSku = colorForSize?.sku;
                        const isColorInWishlist = isVariantInWishlist(variantSku);
                        
                        return (
                          <button
                            key={colorName}
                            onClick={() => handleColorSelect(colorName)}
                            className={`relative w-12 h-12 rounded border-2 flex items-center justify-center overflow-hidden transition-all ${
                              selectedColor === colorName
                                ? "border-gray-900 ring-1 ring-gray-900"
                                : "border-gray-300 hover:border-gray-400"
                            }`}
                            title={colorName}
                          >
                            <div 
                              className="w-8 h-8 rounded"
                              style={{ backgroundColor: colorInfo.hexCode }}
                            />
                            {/* UPDATED: Wishlist indicator for color variants */}
                            {isColorInWishlist && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                <Heart size={8} fill="white" stroke="white" />
                              </div>
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500">No colors available for this size</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Please select a size first to see available colors</p>
                )}
              </div>

              {/* Selected variant info - UPDATED with wishlist status */}
              {selectedVariant && (
                <div className="mb-6 p-3 bg-gray-50 rounded text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-700">
                        <strong>Selected:</strong> {selectedSize} - {selectedColor} - ₹{selectedVariant.pricing?.salePrice || selectedVariant.pricing?.basePrice}
                      </p>
                      <p className="text-gray-600">
                        Stock: {selectedVariant.inventory?.stock || 0} available
                      </p>
                    </div>
                    {/* UPDATED: Show wishlist status for current variant */}
                    {isFavorite && (
                      <div className="flex items-center text-red-500 text-xs">
                        <Heart size={12} fill="currentColor" />
                        <span className="ml-1">In Wishlist</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {selectedSize && !selectedVariant && (
                <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                  <p className="text-yellow-700">
                    Please select an available color for size {selectedSize}
                  </p>
                </div>
              )}

              {/* Add to Cart */}
              <div className="flex gap-3 mb-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={toggleFavorite}
                  className={`flex-shrink-0 w-12 h-12 border flex items-center justify-center transition-colors ${
                    isFavorite 
                      ? "border-red-300 bg-red-50 text-red-500 hover:bg-red-100" 
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <Heart size={20} fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => isInCart ? removeProductFromCart() : addProductToCart()}
                  className={`flex-1 py-3 px-6 font-medium text-white flex items-center justify-center gap-2 ${
                    !selectedVariant || (selectedVariant.inventory?.stock || 0) <= 0
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gray-900 hover:bg-gray-800"
                  } transition-colors`}
                  disabled={isLoading || !selectedVariant || (selectedVariant.inventory?.stock || 0) <= 0}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : !selectedVariant || (selectedVariant.inventory?.stock || 0) <= 0 ? (
                    <span>Out of Stock</span>
                  ) : isInCart ? (
                    <span>REMOVE FROM CART</span>
                  ) : (
                    <span>ADD TO CART</span>
                  )}
                </motion.button>
              </div>

              {/* Combo Offers */}
              {productData.hasComboOffers && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Special Combo Deals</h3>
                    <span className="text-sm text-gray-500">Save more when you buy together</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {productData.comboOffers.map((combo, index) => {
                      const isComboInCart = cartContainsCombo(combo.name);

                      return (
                        <motion.div 
                          key={combo._id} 
                          className="relative bg-white border-2 border-gray-100 rounded-xl p-5 hover:border-blue-200 hover:shadow-md transition-all duration-200"
                          whileHover={{ y: -2 }}
                        >
                          {combo.hasBadge && (
                            <div className="absolute -top-2 -right-2">
                              <span className={`px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${
                                combo.badgeColor === 'red' 
                                  ? 'bg-red-500 text-white'
                                  : combo.badgeColor === 'blue'
                                    ? 'bg-blue-500 text-white' 
                                    : 'bg-green-500 text-white'
                              }`}>
                                {combo.badgeText}
                              </span>
                            </div>
                          )}

                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-base">{combo.name}</h4>
                              {combo.description && (
                                <p className="text-sm text-gray-500 mt-1">{combo.description}</p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">Includes:</span>
                              <div className="flex items-center gap-1">
                                {combo.sizeDisplay.split(' + ').map((sizeText, i) => (
                                  <span key={i} className="flex items-center">
                                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                      {sizeText}
                                    </span>
                                    {i < combo.sizeDisplay.split(' + ').length - 1 && (
                                      <span className="mx-1 text-gray-400">+</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-xl font-bold text-gray-900">
                                    {combo.formattedComboPrice}
                                  </span>
                                  {combo.hasDiscount && (
                                    <span className="text-sm line-through text-gray-400">
                                      {combo.formattedIndividualTotal}
                                    </span>
                                  )}
                                </div>
                                {combo.hasDiscount && combo.savingsPercentage > 0 && (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                    {combo.savingsPercentage}% OFF
                                  </span>
                                )}
                              </div>
                              
                              {combo.hasDiscount && (
                                <p className="text-sm text-green-600 font-medium">
                                  You save {combo.formattedSavings}
                                </p>
                              )}
                            </div>
                            
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => addComboToCart(combo)}
                              disabled={combo.buttonState !== 'available' || isLoading || isComboInCart}
                              className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all text-sm ${
                                combo.buttonState !== 'available' 
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : isComboInCart
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : 'bg-gray-900 text-white hover:bg-gray-800 shadow-sm'
                              }`}
                            >
                              {isComboInCart ? '✓ Added to Cart' : combo.buttonText}
                            </motion.button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* UPDATED: Wishlist summary for logged-in users */}
              {isLoggedIn && productData.wishlist && productData.wishlist.isInWishlist && (
                <div className="mb-6 p-3 bg-pink-50 border border-pink-200 rounded-lg">
                  <div className="flex items-center gap-2 text-pink-700">
                    <Heart size={16} fill="currentColor" />
                    <span className="text-sm font-medium">
                      {productData.wishlist.totalWishlistedVariants > 0 
                        ? `${productData.wishlist.totalWishlistedVariants} variant${productData.wishlist.totalWishlistedVariants > 1 ? 's' : ''} in your wishlist`
                        : 'Product in your wishlist'
                      }
                    </span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <details className="group">
                  <summary className="flex items-center justify-between py-3 border-b border-gray-200 cursor-pointer">
                    <span className="font-medium">Description</span>
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="py-3 text-sm text-gray-600">
                    {product.description}
                  </div>
                </details>

                <details className="group">
                  <summary className="flex items-center justify-between py-3 border-b border-gray-200 cursor-pointer">
                    <span className="font-medium">Shipping & Returns</span>
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="py-3 text-sm text-gray-600">
                    <div className="space-y-2">
                      <p><strong>Free Shipping:</strong> On orders above ₹500</p>
                      <p><strong>Returns:</strong> 30 days return policy</p>
                      <p><strong>Exchange:</strong> Size exchange available within 7 days</p>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;